import {
    forwardRef,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'

import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, ExternalLink, File, ShieldCheck, Trash2, Upload, X } from 'lucide-react'

import Overlay from '../../../../Components/Overlay'
import AnalyzedView from './Components/AnalyzedView'
import uploadFileMutation from './services/upload-file'
import getAdditionalDocumentUrl from './services/get-document-url'

const splitFileName = (filename) => {
    const lastDot = filename.lastIndexOf('.')
    if (lastDot === -1) return { name: filename, extension: '' }
    return { name: filename.slice(0, lastDot), extension: filename.slice(lastDot + 1) }
}

// Non-Immigrant Visa Holder I-94 documents must be non-expired — spell that out on
// the upload page. Keyed by the page's documentOption (the flow step's accepted
// document type code).
const NON_EXPIRED_NOTES = {
    I94: 'Your I-94 must be non-expired. Expired I-94 records cannot be accepted — please download your latest I-94 from the CBP website.',
    I797A_APPROVAL: 'The I-94 section attached to your I-797A must be non-expired. Expired I-94 records cannot be accepted.',
}

const AdditionalDocument = forwardRef(({ data, requestKey, title, description, documentId, documentOption, analyzeId }, ref) => {

    const [value, setValue] = useState(null)
    const [submittedKey, setSubmittedKey] = useState(data?.documentKey ?? null)
    const [keyReadyToSubmit, setKeyReadyToSubmit] = useState(null)
    const [error, setError] = useState('')
    const [isViewLoading, setIsViewLoading] = useState(false)

    const fileInputRef = useRef()

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            if (keyReadyToSubmit) {
                setError('')
                return {
                    isValid: true,
                    value: {
                        documentKey: keyReadyToSubmit,
                        documentId,
                        analyzeId,
                    },
                }
            }

            if (submittedKey) {
                setError('')
                return {
                    isValid: true,
                    value: {
                        documentKey: submittedKey,
                        documentId,
                        analyzeId,
                    },
                }
            }

            setError('Please upload a document to proceed')
            return {
                isValid: false,
                value: null,
            }
        },
    }))

    const {
        isPending: mutationIsPending,
        error: mutationError,
        mutate: uploadFile,
    } = useMutation({
        mutationFn: uploadFileMutation,
        onSuccess: (response) => {
            setKeyReadyToSubmit(response.key)
        },
    })

    const handleFile = (file) => {
        if (!file) return
        const { name, extension } = splitFileName(file.name)
        setValue({ file, originalName: file.name, fileName: name, fileExtension: extension })
        setError('')
    }

    const handleDrop = (e) => {
        e.preventDefault()
        handleFile(e.dataTransfer.files[0])
    }

    const handleFileSelect = (e) => {
        e.preventDefault()
        handleFile(e.target.files[0])
    }

    const handleLocalDelete = () => {
        setValue(null)
        setSubmittedKey(null)
        setKeyReadyToSubmit(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleUpload = () => {
        if (!value) return
        uploadFile({
            requestKey,
            file: value.file,
            fileName: value.fileName,
            fileExtension: value.fileExtension,
        })
    }

    // The uploaded document lives under S3 assets/private and can only be opened with a presigned
    // URL. Fetch one lazily when "View file" is clicked, then open it in a new tab.
    const handleView = async (documentKey) => {
        if (!documentKey || isViewLoading) return
        setIsViewLoading(true)
        try {
            const { url } = await getAdditionalDocumentUrl({ requestKey, documentKey })
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer')
            }
        }
        catch (e) {
            // Best-effort — the document could not be resolved.
        }
        finally {
            setIsViewLoading(false)
        }
    }

    if (analyzeId) {
        return <AnalyzedView title={title} submittedKey={submittedKey} requestKey={requestKey} />
    }

    return (
        <div className="flex items-center justify-center px-4 py-10">
            <div className="max-w-3xl w-full bg-white shadow-xl rounded-2xl p-8 space-y-6">

                {(title || description) && (
                    <div className="space-y-2">
                        {title && (
                            <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
                        )}
                        {description && (
                            <p className="text-gray-700 text-base">{description}</p>
                        )}
                    </div>
                )}

                {documentOption && (
                    <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/70 to-slate-50 p-4">
                        <div className="flex items-center gap-2 mb-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <ShieldCheck size={13} className="text-blue-600" />
                            </div>
                            <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">
                                Accepted Documents
                            </span>
                        </div>
                        <div className="flex items-start gap-2.5">
                            <CheckCircle2 size={15} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-600 leading-snug">{documentOption}</span>
                        </div>
                    </div>
                )}

                {NON_EXPIRED_NOTES[documentOption] && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex items-start gap-2.5">
                        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-800 leading-snug">
                            <span className="font-semibold">Non-Expired Document Required: </span>
                            {NON_EXPIRED_NOTES[documentOption]}
                        </p>
                    </div>
                )}

                <div className="space-y-3">
                    {submittedKey && (
                        <div className="flex items-center justify-between rounded-xl border p-4 bg-white shadow-sm">
                            <div>
                                <p className="text-sm text-gray-600">Data is already uploaded</p>
                                <button
                                    type="button"
                                    onClick={() => handleView(submittedKey)}
                                    disabled={isViewLoading}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ExternalLink size={16} />
                                    {isViewLoading ? 'Opening…' : 'View file'}
                                </button>
                            </div>
                            <button
                                onClick={handleLocalDelete}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-red-700 active:scale-95 transition"
                            >
                                <Trash2 size={16} />
                                Delete
                            </button>
                        </div>
                    )}

                    {!submittedKey && keyReadyToSubmit && (
                        <div className="flex items-center justify-between rounded-xl border p-4 bg-white shadow-sm">
                            <div>
                                <p className="text-sm text-gray-600">Data is ready to submit</p>
                                <button
                                    type="button"
                                    onClick={() => handleView(keyReadyToSubmit)}
                                    disabled={isViewLoading}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <ExternalLink size={16} />
                                    {isViewLoading ? 'Opening…' : 'View file'}
                                </button>
                            </div>
                            <button
                                onClick={handleLocalDelete}
                                className="inline-flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-red-700 active:scale-95 transition"
                            >
                                <Trash2 size={16} />
                                Delete
                            </button>
                        </div>
                    )}

                    {!submittedKey && !keyReadyToSubmit && (
                        <div className="space-y-3">
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={handleDrop}
                                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition hover:bg-gray-50 ${error ? 'border-red-500' : 'border-gray-300'}`}
                            >
                                <p className="text-gray-600 font-medium">Drag and drop or click to select the file</p>
                                <p className="text-gray-400 text-sm mt-1">Supported file types: .pdf, .png, .jpg</p>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="application/pdf,image/png,image/jpeg"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                />
                            </div>

                            {value && (
                                <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200 shadow-sm">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <File className="text-gray-500 flex-shrink-0" size={20} />
                                        <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]" title={value.originalName}>
                                            {value.originalName}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                            onClick={handleUpload}
                                            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-blue-700 active:scale-95 transition"
                                        >
                                            <Upload size={16} />
                                            Upload
                                        </button>
                                        <button
                                            onClick={handleLocalDelete}
                                            className="inline-flex items-center gap-1 rounded-lg bg-red-50 text-red-600 px-3 py-1.5 text-sm font-medium hover:bg-red-100 active:scale-95 transition"
                                        >
                                            <X size={16} />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {error && (
                    <p className="text-sm text-red-600">{error}</p>
                )}

                {mutationError && (
                    <p className="text-sm text-red-600">Upload failed. Please try again.</p>
                )}

                {mutationIsPending && <Overlay />}

            </div>
        </div>
    )

})

export default AdditionalDocument
